import { memo, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import TextInput from '../../../core/components/input/TextInput';
import { AppContext } from '../../../core/context';
import usePriceFilter from '../../hooks/usePriceFilter';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import styles from './FilterPrice.module.scss';

// cspell:words stepsize

const FilterPrice = ({
    name,
    priceRange,
    defaultExpand,
}: {
    name: string;
    priceRange: { min: number; max: number };
    defaultExpand?: boolean;
}) => {
    const { currency } = useContext(AppContext);

    const {
        minPrice,
        maxPrice,
        stepSize,
        applyFilter,
        updateMinPrice,
        updateMaxPrice,
        increaseMinPrice,
        increaseMaxPrice,
        decreaseMinPrice,
        decreaseMaxPrice,
    } = usePriceFilter(priceRange);

    if (priceRange.min >= priceRange.max) {
        return null;
    }

    return (
        <CollapsibleContent
            expanded={defaultExpand}
            smallIcon
            title={
                <CollapsibleSideBarFilterTitle>
                    {name}
                </CollapsibleSideBarFilterTitle>
            }
            endArrow
            filterIcon
        >
            <div className={styles.filter}>
                <div className={styles.priceFilter}>
                    <TextInput
                        hasStartAdornment
                        aria-label="min"
                        type="number"
                        id="min"
                        value={minPrice?.toString() ?? ''}
                        placeholder={`${priceRange.min}`}
                        startAdornment={<FormattedMessage id={currency} />}
                        data-test-class="minValueInput"
                        data-test-stepsize={stepSize}
                        onChange={e => {
                            const { value } = e.target;
                            if (!value) {
                                updateMinPrice();
                            } else {
                                updateMinPrice(+value);
                            }
                        }}
                        onKeyDown={e => {
                            if (['ArrowUp', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                                increaseMinPrice();
                            } else if (
                                ['ArrowDown', 'ArrowLeft'].includes(e.key)
                            ) {
                                e.preventDefault();
                                decreaseMinPrice();
                            } else if (e.key === 'Enter') {
                                e.preventDefault();
                                applyFilter();
                            }
                        }}
                        onBlur={applyFilter}
                    />
                </div>
                &ndash;
                <div className={styles.priceFilter}>
                    <TextInput
                        hasStartAdornment
                        aria-label="max"
                        type="number"
                        id="max"
                        value={maxPrice?.toString() ?? ''}
                        placeholder={`${priceRange.max}`}
                        startAdornment={<FormattedMessage id={currency} />}
                        data-test-class="maxValueInput"
                        onChange={e => {
                            const { value } = e.target;
                            if (!value) {
                                updateMaxPrice();
                            } else {
                                updateMaxPrice(+value);
                            }
                        }}
                        onKeyDown={e => {
                            if (['ArrowUp', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                                increaseMaxPrice();
                            } else if (
                                ['ArrowDown', 'ArrowLeft'].includes(e.key)
                            ) {
                                e.preventDefault();
                                decreaseMaxPrice();
                            } else if (e.key === 'Enter') {
                                e.preventDefault();
                                applyFilter();
                            }
                        }}
                        onBlur={applyFilter}
                    />
                </div>
            </div>
        </CollapsibleContent>
    );
};

export default memo(FilterPrice);
